/**
 * ResetPasswordForm -- Standalone reset-password page.
 *
 * This is the LAST step of two flows that both email the user a link to
 * `{site_url}/reset-password?token=XXX`:
 *   1. New-buyer onboarding (guest purchase → "set your password")
 *   2. Forgot-password recovery
 *
 * Full-page centered layout matching ForgotPasswordForm. Reads the token from
 * the URL, validates it against the cloud on mount, and — if live — shows a
 * New password + Confirm password form. On success the user is sent to /login
 * (a reset does NOT log them in).
 *
 * States (no happy-path-only):
 *   - validating  → spinner
 *   - invalid     → dead-link error + link to /forgot-password
 *   - valid       → the two-field form
 *   - submitting  → button spinner
 *   - error       → toast.error(backend message), form kept for a retry
 *   - success     → toast.success + navigate('/login')
 */

import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { z } from 'zod'
import { ArrowLeft, KeyRound } from 'lucide-react'
import { toast } from 'sonner'
import { useResetPassword, validateResetToken } from '@/lib/auth'
import { Button } from '@/shared/components/forms/Button'
import { Input } from '@/shared/components/forms/Input'
import { LoadingSpinner } from '@/shared/components/feedback/LoadingSpinner'

/**
 * Same 8–256 floor the backend enforces (do_reset_password), plus a client-side
 * "passwords match" check so the user gets instant feedback before a round-trip.
 */
const resetSchema = z
  .object({
    newPassword: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .max(256, 'Password must be at most 256 characters'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

/** Token-validation lifecycle: still checking, live, or dead. */
type TokenState = 'validating' | 'valid' | 'invalid'

export function ResetPasswordForm() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const navigate = useNavigate()
  const resetMutation = useResetPassword()

  const [tokenState, setTokenState] = useState<TokenState>(
    // No token at all → immediately invalid, skip the round-trip entirely.
    token ? 'validating' : 'invalid',
  )
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [validationError, setValidationError] = useState<string | null>(null)

  // Validate the token once on mount. This is a one-shot side effect, not
  // ongoing server-state sync — the token either resolves the form or the
  // dead-link state, and we never re-poll it.
  useEffect(() => {
    if (!token) return
    let cancelled = false
    void (async () => {
      const result = await validateResetToken(token)
      if (cancelled) return
      setTokenState(result.success ? 'valid' : 'invalid')
    })()
    return () => {
      cancelled = true
    }
  }, [token])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setValidationError(null)

    if (!token) return

    const result = resetSchema.safeParse({ newPassword, confirmPassword })
    if (!result.success) {
      setValidationError(result.error.issues[0]?.message ?? 'Invalid password')
      return
    }

    const response = await resetMutation.mutateAsync({
      token,
      newPassword: result.data.newPassword,
    })

    if (response.success) {
      toast.success('Password set! You can now sign in.')
      navigate('/login')
    } else {
      toast.error(response.error || 'Password reset failed')
    }
  }

  return (
    <div className="flex min-h-[calc(100vh-2.5rem)] items-center justify-center p-4">
      <div className="w-full max-w-sm rounded-xl bg-ue-bg-dark/80 border border-ue-border-medium/60 p-6">
        {tokenState === 'validating' ? (
          /* Validating state */
          <div className="flex items-center justify-center gap-2 py-6 text-xs text-ue-text-secondary">
            <LoadingSpinner size="sm" />
            <span>Checking your reset link…</span>
          </div>
        ) : tokenState === 'invalid' ? (
          /* Dead-link state */
          <>
            <div className="p-3 bg-ue-accent-red/10 border border-ue-accent-red/20 rounded text-ue-accent-red text-xs mb-4">
              <div className="flex items-center gap-1.5 mb-1">
                <KeyRound className="w-3.5 h-3.5 shrink-0" />
                <span className="font-medium">Reset link invalid</span>
              </div>
              <p className="text-ue-accent-red/80">
                This reset link is invalid or has expired. Request a fresh one
                and we'll email you a new link.
              </p>
            </div>
            <Link
              to="/forgot-password"
              className="flex items-center gap-1.5 text-xs text-ue-text-tertiary hover:text-ue-text-secondary transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Request a new reset link
            </Link>
          </>
        ) : (
          /* Form state (token valid) */
          <>
            <h2 className="text-sm font-medium text-ue-text-primary mb-1">
              Set a new password
            </h2>
            <p className="text-xs text-ue-text-secondary mb-4">
              Choose a new password for your account. It must be at least 8
              characters.
            </p>

            <form onSubmit={handleSubmit} className="space-y-3">
              <Input
                type="password"
                label="New password"
                placeholder="At least 8 characters"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={resetMutation.isPending}
              />
              <Input
                type="password"
                label="Confirm password"
                placeholder="Re-enter your new password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                error={validationError ?? undefined}
                disabled={resetMutation.isPending}
              />
              <Button
                type="submit"
                loading={resetMutation.isPending}
                className="w-full"
              >
                Set New Password
              </Button>
            </form>

            <Link
              to="/login"
              className="mt-4 flex items-center gap-1.5 text-xs text-ue-text-tertiary hover:text-ue-text-secondary transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to sign in
            </Link>
          </>
        )}
      </div>
    </div>
  )
}
